import React, { useEffect, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, Navigation } from "swiper/modules";
import ProductDescriptionTabs from "./component/ProductDescriptionTabs";
import ProductSlide from "./component/ProductSlide";
import { useApiStore } from "./hooks/ApiStoreContext";
import { useAuth } from "./hooks/AuthProvider";
import { useCart } from "./hooks/CartContext";
import ZoomImage from "./component/ZoomImage";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import "./ProductDetail.css";
import { useNavigate } from "react-router-dom";

const ProductDetail = () => {
  const apiStore = useApiStore();
  const {userId}=useAuth();
  const {addToCartContext} = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const productId = new URLSearchParams(window.location.search).get("productId");
  const [showAddToCartPopup, setShowAddToCartPopup] = useState(false);
  //const [zoomStyles, setZoomStyles] = useState({});
  const [similarProducts, setSimilarProducts] = useState([]);

  const MOQQuantityMap = {
    1: "Dozen",
    2: "Piece",
    3: "Pair",
    4: "Box",
    5: "Packet",
  };

  // Set initial quantity based on MOQ when product is loaded
  useEffect(() => {
    if (product.moq && product.moq > 0) {
      setQuantity(product.moq);
    }
  }, [product.moq]);

  useEffect(() => {
    if (!productId) {
      console.log("Product ID is missing from the query string.");
      return;
    }

    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/product/${productId}`);
        setProduct(response.data);
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error("Error fetching product:", error);
        }
      }
    };

    fetchProduct();
  }, [apiBaseUrl, productId]);

  // Fetch similar products based on categoryId
  useEffect(() => {
    if (product.categoryId) {
      const fetchSimilarProducts = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/Product/ByCategory?categoryId=${product.categoryId}`);
          // Filter out the current product from similar products
          const filteredProducts = response.data.filter((p) => p.productId !== product.productId);
          setSimilarProducts(filteredProducts);
        } catch (error) {
          console.error("Error fetching similar products:", error);
        }
      };

      fetchSimilarProducts();
    }
  }, [apiBaseUrl, product.categoryId, product.productId]);

  const handleQuantityChange = (value) => {
    const newQuantity = typeof value === "number" ? quantity + value : parseInt(value) || product.moq || 1;

    // Enforce MOQ when decreasing or setting quantity
    if (newQuantity < (product.moq || 1)) {
      alert(`Minimum quantity ${product.moq ?? 1} needs to be purchased.`);
      setQuantity(product.moq || 1);
    } else {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    // Check if quantity meets MOQ before adding to cart
    if (quantity < (product.moq || 1)) {
      //alert(`Minimum quantity ${product.moq ?? 1} needs to be purchased.`);
      setQuantity(product.moq || 1);
      return;
    }

    try {
      let loginUserId = userId;
      if (!loginUserId) {
        showLoginModal();
        return;
      }
      
      let cartItem={
        shoppingCartId: 0,
        productId: Number(productId),
        quantity: Number(quantity),
        userId: Number(loginUserId)
      };

      await apiStore.post("/users/AddToCart", cartItem);

      addToCartContext(cartItem);
      setShowAddToCartPopup(true);
    } catch (error) {
      console.error("Error adding product to cart:", error);
      if (error.status === 401) {
        showLoginModal();
      }
    }
  };

  const showLoginModal = () => {
    const loginModal = document.getElementById("login");
    const modal = new window.bootstrap.Modal(loginModal);
    modal.show();
  };

  const handleGoToCart = () => {
    // Navigate to the cart page
    //window.location.href = "/view-cart";
    navigate("/view-cart");
  };

  // Zoom handlers
  // const handleMouseEnter = () => {
  //   setZoomStyles({ transform: "scale(1.5)" }); // Initial zoom
  // };

  // const handleMouseLeave = () => {
  //   setZoomStyles({}); // Reset zoom
  // };

  // const handleMouseMove = (e) => {
  //   const { left, top, width, height } = e.target.getBoundingClientRect();
  //   const x = e.clientX - left; // Mouse X relative to image
  //   const y = e.clientY - top; // Mouse Y relative to image
  //   // Calculate percentage of mouse position (0 to 1)
  //   const xPercent = x / width;
  //   const yPercent = y / height;
  //   // Translate to center the hovered area (adjust for zoom scale)
  //   const translateX = (0.5 - xPercent) * width * 0.5; // 0.5 scales movement for 1.5x zoom
  //   const translateY = (0.5 - yPercent) * height * 0.5;
  //   setZoomStyles({
  //     transform: `scale(1.5) translate(${translateX}px, ${translateY}px)`,
  //   });
  // };

  return (
    <>
      <section className="flat-spacing-4 pt_1">
        <div className="tf-main-product section-image-zoom">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <div className="tf-product-media-wrap sticky-top">
                  <div className="thumbs-slider">
                    {/* Thumbnail Slider */}
                    <Swiper onSwiper={setThumbsSwiper} spaceBetween={10} slidesPerView={4} direction="vertical" modules={[Thumbs]} className="tf-product-media-thumbs">
                      {product.productImages?.map((image) => (
                        <SwiperSlide key={image.productImageId}>
                          <div className="item">
                            <img className="lazyload" src={image.imageSource} alt={product.name || "Thumbnail"} />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    {/* Main Slider */}
                    <Swiper
                      spaceBetween={10}
                      thumbs={{ swiper: thumbsSwiper }}
                      modules={[Thumbs, Navigation]}
                      navigation={{
                        nextEl: ".thumbs-next",
                        prevEl: ".thumbs-prev",
                      }}
                      className="tf-product-media-main">
                      {product.productImages?.map((image) => (
                        <SwiperSlide key={image.productImageId}>
                          <div className="item">
                            {/* <img className="tf-image-zoom lazyload" src={image.imageSource} alt={product.name || "Product Image"} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onMouseMove={handleMouseMove} style={zoomStyles} /> */}
                            <ZoomImage src={image.imageSource} alt={product.name || "Product Image"} zoomScale={3.5} />
                          </div>
                        </SwiperSlide>
                      ))}
                      <div className="swiper-button-next button-style-arrow thumbs-next"></div>
                      <div className="swiper-button-prev button-style-arrow thumbs-prev"></div>
                    </Swiper>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="tf-product-info-wrap position-relative">
                  <div className="tf-zoom-main"></div>
                  <div className="tf-product-info-list other-image-zoom">
                    <div className="tf-product-info-title">
                      <h5>{product.name}</h5>
                    </div>
                    <div className="tf-product-info-badges">
                      <div className="badges">Best seller</div>
                      <div className="product-status-content">
                        <i className="icon-lightning"></i>
                        <p className="fw-6">Selling fast!</p>
                      </div>
                    </div>
                    <div className="tf-product-info-price">
                      {product.discount === 0 ? (
                        <div className="price-on-sale">₹{Number(product.totalPrice ?? 0).toFixed(2)}</div>
                      ) : (
                        <>
                          <div className="price-on-sale">₹{Number((product.totalPrice) ?? 0).toFixed(2)}</div>
                          <div className="compare-at-price">₹{Number(product.price ?? 0).toFixed(2)}</div>
                          <div className="badges-on-sale">
                            <span>{product.discount}</span>% OFF
                          </div>
                        </>
                      )}

                      {product.rateOfPiece !== "" && <div className="rate-of-piece">{product.rateOfPiece}</div>}
                    </div>
                    <div className="tf-product-info-countdown">
                      <div className="countdown-wrap">
                        <div className="countdown-title">
                          <i className="icon-time tf-ani-tada"></i>
                          {product.stock > 0 ? (
                            <p>
                              Hurry! Only {product.stock} {product.stock === 1 ? "item" : "items"} left in stock!
                            </p>
                          ) : (
                            <p style={{ color: "red" }}>
                              {product?.outOfStockMessage?.trim() ? product.outOfStockMessage : "Out of stock"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="tf-product-info-quantity">
                      <div className="quantity-title fw-6">Quantity</div>
                      <div className="wg-quantity">
                        <span className="btn-quantity minus-btn" onClick={() => handleQuantityChange(-1)}>
                          -
                        </span>
                        <input type="text" name="number" value={quantity} onChange={(e) => handleQuantityChange(e.target.value)} />
                        <span className="btn-quantity plus-btn" onClick={() => handleQuantityChange(1)}>
                          +
                        </span>
                      </div>
                    </div>
                    <div className="tf-product-info-buy-button">
                      <form className="">
                        <a href="#" style={product.stock <= 0 || quantity > product.stock ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" } : {}} className="tf-btn btn-fill justify-content-center fw-6 fs-16 flex-grow-1 animate-hover-btn" onClick={product.stock > 0 && quantity <= product.stock ? handleAddToCart : null}>
                          <span>{product.stock <= 0 ? "Out of stock" : quantity > product.stock ? `Only ${product.stock} items available` : "Add to cart - "}</span>
                          {product.stock > 0 && quantity <= product.stock && <span className="tf-qty-price">₹{(product.totalPrice * quantity).toFixed(2)}</span>}
                        </a>
                      </form>
                    </div>

                    <div className="row product-details-row">
                      <div className="col-md-12 product-details-col">
                        <div className="tf-product-info-table">
                          <table className="product-details-table">
                            <tbody>
                              <tr className="table-header">
                                <td className="label fw-6" colSpan={2} align="center">
                                  <h6>Product Details</h6>
                                </td>
                              </tr>
                              {/* <tr>
                                <td className="label fw-6">GST Included in Price</td>
                                <td className={`value ${product.gstIncluded ? "gst-yes" : "gst-no"}`}>
                                  <span className="gst-icon">{product.gstIncluded ? "✔" : "✖"}</span>
                                </td>
                              </tr> */}
                              <tr>
                                <td className="label fw-6">GST</td>
                                <td className="value">
                                  {product.gstAmountAfterDiscount === "N/A" || product.gstAmountAfterDiscount === null || product.gstAmountAfterDiscount === undefined 
                                    ? "N/A" 
                                    : <>{product.gst}% <i className="label fw-6" style={{fontStyle: "italic"}}>(₹{(product.gstAmountAfterDiscount * quantity).toFixed(2)} included in price)</i></>}
                                </td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Product Code</td>
                                <td className="value">{product.code || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">MOQ</td>
                                <td className="value">
                                  {product.moq || product.moq === 0 ? product.moq : "N/A"} {product.moqQuantity ? MOQQuantityMap[product.moqQuantity] || "N/A" : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Gender</td>
                                <td className="value">{product.gender || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Age Group</td>
                                <td className="value">{product.ageGroup || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Fabric</td>
                                <td className="value">{product.fabric || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Color</td>
                                <td className="value">{product.color || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Occasion</td>
                                <td className="value">{product.occasion || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Size</td>
                                <td className="value">{product.size || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Pattern</td>
                                <td className="value">{product.pattern || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Set Content</td>
                                <td className="value">{product.setContent || "N/A"}</td>
                              </tr>
                              <tr>
                                <td className="label fw-6">Design Number</td>
                                <td className="value">{product.designNumber || "N/A"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="tf-product-info-delivery-return">
                      <div className="row">
                        <div className="col-xl-6 col-12">
                          <div className="tf-product-delivery">
                            <div className="icon">
                              <i className="icon-delivery-time"></i>
                            </div>
                            <p>
                              Estimate delivery times: <span className="fw-7">3-26 days</span> (India)
                            </p>
                          </div>
                        </div>
                        <div className="col-xl-6 col-12">
                          <div className="tf-product-delivery mb-0">
                            <div className="icon">
                              <i className="icon-return-order"></i>
                            </div>
                            <p>
                              Return within <span className="fw-7">30 days</span> of purchase.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ProductDescriptionTabs description={product.description} />
      <ProductSlide title="Similar Products" products={similarProducts} />
      <ProductSlide title="Recently Viewed" />
      {showAddToCartPopup && (
        <div className="popup-container">
          <div className="popup-content">
            <h4>Item Added to Cart!</h4>
            <p>You can view your cart or continue shopping.</p>
            <button onClick={handleGoToCart}>Go to Cart</button>
            <button onClick={() => setShowAddToCartPopup(false)}>Continue Shopping</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetail;
